import { EGender } from 'affiny-core/modules/account/types';
import {
  getMainPicture,
  mainPictureFirst,
  selectPictureByUri,
} from 'affiny-core/redux/selectors/picturesSelectors';
import filter from 'lodash/filter';
import get from 'lodash/get';
import { capitalize } from '../../helpers/string/capitalize';
import { IPicture } from '../../modules/pictures/types';
import { IRootState } from '../../modules/state';

/* ***** *****  Basic data  ***** ***** */

export const selectAccountId = (state: IRootState) => state.account.id;

export const selectHashedId = (state: IRootState) => state.account.hashedId;

export const selectAccountAttributes = (state: IRootState) =>
  state.account.attributes;

export const selectAccountAttributeElement = (
  state: IRootState,
  element: string
) => state.account.attributes?.[element];

export const selectAccountIsPrivatePicture = (state: IRootState): boolean => {
  const attributes = selectAccountAttributes(state);

  return get(attributes, 'privacy.privatePicture', false);
};

export const selectAccountAnnounce = (state: IRootState) =>
  state.account.profile && state.account.profile.announce;

export const selectAccountSearch = (state: IRootState) => ({
  gender: selectAccountAttributeElement(state, 'gender') as EGender,
  wantedGender: selectAccountAttributeElement(state, 'wantedGender') as EGender,
});

export const selectMyCity = (state: IRootState) => {
  const city = state.account.attributes?.geo?.city.name;

  return capitalize(city);
};

/* ***** *****  Pictures  ***** ***** */

export const hasPicturesFilled = (state: IRootState) =>
  Array.isArray(state.account.pictures) && state.account.pictures.length > 0;

export const selectPictures = (state: IRootState): IPicture[] =>
  get(state, 'account.pictures', [])
    .map((pictureId: string) => selectPictureByUri(state, pictureId))
    .sort(mainPictureFirst);

const numberOfPictures = (state: IRootState) =>
  get(state, 'account.pictures', []).length;

export const selectSecondaryPictures = (state: IRootState): IPicture[] =>
  filter(selectPictures(state), {
    attributes: { isMainPicture: false },
  });

export const hasMorePicturesThan = (state: IRootState, minNumber: number) =>
  numberOfPictures(state) > minNumber;

export const hasLessPicturesThan = (state: IRootState, maxNumber: number) =>
  numberOfPictures(state) < maxNumber;

export const selectMainPicture = (state: IRootState) =>
  getMainPicture(selectPictures(state));

export const selectIsPrivatePicture = (state: IRootState) =>
  state.account.attributes &&
  state.account.attributes.privacy &&
  state.account.attributes.privacy.privatePicture;

/* ***** *****  Settings   ***** ***** */

export const selectOptins = (state: IRootState) => state.account.optins;
